import { useMemo, useState } from "react";
import { Button, Tag, message, Popconfirm } from "antd";
import { PlusOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import AddHandoverRequestModal from "../../components/modals/AddHandoverRequestModal";
import {
  useGetHandoverRequestsQuery,
  useAcceptHandoverRequestMutation,
  useRejectHandoverRequestMutation,
} from "../../services/handover";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface HandoverRecord {
  key:                string;
  dateTime?:          string;
  requestedBy?:       string;
  requestedById?:     string;
  handoverTo?:        string;
  targetEmployeeId?:  string;
  branch?:            string;
  amount?:            number;
  status?:            string;
  remark?:            string;
}

const ELEVATED_USER_TYPES = ['SUPERADMIN', 'DIRECTOR'];

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

/* ─── Constants ──────────────────────────────────────────────────────── */

const STATUS_COLOR: Record<string, string> = {
  pending:  'orange',
  approved: 'green',
  accepted: 'green',
  rejected: 'red',
  declined: 'red',
};

const renderDateTime = (dateTime?: string) => {
  if (!dateTime) return '-';
  const dt = new Date(dateTime);
  if (isNaN(dt.getTime())) return dateTime;
  return (
    <div>
      <div>{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      <div style={{ fontSize: 12, opacity: 0.65 }}>{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const Handover = () => {
  const [page,  setPage]  = useState(1);
  const [limit, setLimit] = useState(10);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { data: handoverResp, isFetching } = useGetHandoverRequestsQuery({ page, limit });
  const apiData = handoverResp as any;

  const currentUser = getCurrentUser();
  const currentUserId = currentUser?._id || currentUser?.id;
  const isElevatedUser = ELEVATED_USER_TYPES.includes(String(currentUser?.userType || '').toUpperCase());

  const [acceptHandoverRequest, { isLoading: accepting }] = useAcceptHandoverRequestMutation();
  const [rejectHandoverRequest, { isLoading: rejecting }] = useRejectHandoverRequestMutation();
  const [respondingId, setRespondingId] = useState<string | null>(null);

  const records = useMemo<HandoverRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((h: any) => ({
      key:               h._id,
      dateTime:          h.dateTime || h.createdAt,
      requestedBy:       h.requestedBy?.name || h.handedBy?.name || h.fromEmployee?.name || h.createdBy?.name,
      requestedById:     h.requestedBy?._id || h.handedBy?._id || h.fromEmployee?._id || h.createdBy?._id,
      handoverTo:        h.handoverTo?.name || h.receivedBy?.name || h.toEmployee?.name || h.employeeId?.name || h.employee?.name,
      targetEmployeeId:  h.employeeId?._id || h.employeeId || h.employee?._id || h.toEmployee?._id,
      branch:            h.branch?.name || h.branchId?.name,
      amount:            h.amount,
      status:            h.status,
      remark:            h.remark || h.note || h.description,
    }));
  }, [apiData]);

  const handleAccept = async (id: string) => {
    setRespondingId(id);
    try {
      await acceptHandoverRequest(id).unwrap();
      // message.success('Handover request accepted');
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setRespondingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setRespondingId(id);
    try {
      await rejectHandoverRequest(id).unwrap();
      // message.success('Handover request rejected');
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setRespondingId(null);
    }
  };

  const columns = [
    { title: 'Date',         dataIndex: 'dateTime',    key: 'dateTime',    render: renderDateTime },
    { title: 'Requested By', dataIndex: 'requestedBy', key: 'requestedBy', render: (v?: string) => v || '-' },
    { title: 'Handover To',  dataIndex: 'handoverTo',  key: 'handoverTo',  render: (v?: string) => v || '-' },
    { title: 'Branch',       dataIndex: 'branch',      key: 'branch',      render: (v?: string) => v || '-' },
    { title: 'Amount',       dataIndex: 'amount',      key: 'amount',
      render: (v?: number) => v === undefined || v === null ? '-' : `₹${v.toLocaleString('en-IN')}` },
    { title: 'Status', dataIndex: 'status', key: 'status', align: 'center' as const,
      render: (v?: string) => v ? <Tag color={STATUS_COLOR[v.toLowerCase()] || 'default'}>{v.toUpperCase()}</Tag> : '-' },
    { title: 'Remark', dataIndex: 'remark', key: 'remark', render: (v?: string) => v || '-' },
    { title: 'Actions', key: 'actions', align: 'center' as const,
      render: (_: unknown, record: HandoverRecord) => {
        const isSender = !!currentUserId && record.requestedById === currentUserId;
        const canRespond = !isSender
          && record.status?.toLowerCase() === 'pending'
          && (isElevatedUser || (!!currentUserId && record.targetEmployeeId === currentUserId));
        if (!canRespond) return '-';
        const isBusy = respondingId === record.key;
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              loading={isBusy && accepting}
              disabled={isBusy && rejecting}
              onClick={() => handleAccept(record.key)}
            >
              Accept
            </Button>
            <Popconfirm
              title="Reject this handover request?"
              onConfirm={() => handleReject(record.key)}
              okText="Reject"
              cancelText="Cancel"
            >
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                loading={isBusy && rejecting}
                disabled={isBusy && accepting}
              >
                Reject
              </Button>
            </Popconfirm>
          </div>
        );
      } },
  ];

  return (
    <div className="handover-page">
      <div className="header-section">
        <h2 style={{ margin: 0 }}>Handover Requests</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          Add Handover Request
        </Button>
      </div>

      <div className="handover-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isFetching}
          pagination={false}
          rowKey={(record: HandoverRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <AddHandoverRequestModal
        visible={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
      />
    </div>
  );
};

export default Handover;
